import { useTranslation } from "react-i18next";
import { FaGlobe } from "react-icons/fa6";
import { useState, useRef, useEffect } from "react";
import { useEnabledLocales } from "../../hooks/useEnabledLocales";
import { useLogin } from "../../hooks/useLogin";
import { useSetUserLocale, TOKEN_KEY } from "../../api/queryHooks/user";
import { FALLBACK_LOCALE, isLocale, type Locale } from "../../i18n/locales";
import { writeChoice } from "../../i18n/localePreference";
import "./LanguageSelector.scss";

const LANGUAGE_META: Record<Locale, { name: string; flag: string }> = {
  en: { name: 'English', flag: '🇬🇧' },
  de: { name: 'Deutsch', flag: '🇩🇪' },
  hu: { name: 'Magyar', flag: '🇭🇺' },
};

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const enabledLocales = useEnabledLocales();
  const { impersonation } = useLogin();
  const setUserLocale = useSetUserLocale();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const resolved = i18n.resolvedLanguage;
  const activeLocale: Locale =
    isLocale(resolved) && enabledLocales.includes(resolved)
      ? resolved
      : (enabledLocales[0] ?? FALLBACK_LOCALE);
  const currentMeta = LANGUAGE_META[activeLocale];

  const changeLanguage = (lng: Locale) => {
    i18n.changeLanguage(lng);
    // Always persisted locally, so the pick survives logout (logout only
    // clears the tokens, not this key) — see i18n/localePreference.ts.
    writeChoice(lng);
    // Gate on the token's own presence rather than useLogin().isLoggedIn:
    // isLoggedIn is derived from useUserData(), which is still `isPending`
    // (neither true nor false) for a beat after a token exists but /users/me
    // hasn't resolved yet. A pick made in that window would otherwise send
    // no PUT, and LocaleProvider could then revert the pick once the row's
    // (different) stored locale arrives. A guest holds a real token too, and
    // /auth/register converts that same row in place, so its locale must
    // reach the row now rather than waiting for registration.
    //
    // Skip the write entirely while impersonating: users_controller.rb
    // already no-ops it server-side (Current.impersonator present), so
    // sending it would just be a pointless request for the admin's own pick
    // against the customer's row.
    if (localStorage.getItem(TOKEN_KEY) !== null && !impersonation) setUserLocale.mutate(lng);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      className={`dropdown language-selector ${isOpen ? 'is-active' : ''}`}
      ref={dropdownRef}
    >
      <div className="dropdown-trigger">
        <button
          className="button is-small"
          aria-haspopup="true"
          aria-controls="language-dropdown-menu"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Select language"
        >
          <span className="icon is-small">
            <FaGlobe />
          </span>
          <span className="language-flag">{currentMeta.flag}</span>
          <span className="language-code">{activeLocale.toUpperCase()}</span>
        </button>
      </div>
      <div className="dropdown-menu" id="language-dropdown-menu" role="menu">
        <div className="dropdown-content">
          {enabledLocales.map((code) => {
            const meta = LANGUAGE_META[code];
            const isActive = code === activeLocale;
            return (
              <button
                key={code}
                type="button"
                className={`dropdown-item ${isActive ? 'is-active' : ''}`}
                onClick={() => changeLanguage(code)}
              >
                <span className="language-flag">{meta.flag}</span>
                <span className="language-name">{meta.name}</span>
                {isActive && (
                  <span className="language-check">✓</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default LanguageSelector;
